import { CREATE_OR_UPDATE_INCORPORATION_REQUEST_API } from "@/constant";
import type { IncorporationRequest } from "@/schemas/incorporation";
import { apiRequest } from "@/utils";

// Response types
export type CreateOrUpdateSuccessResponse = {
  success: true;
  id: number;
  data: IncorporationRequest;
};

export type CreateOrUpdateErrorResponse = {
  success: false;
  error?: string;
  errors?: string[];
};

export type CreateOrUpdateResponse =
  | CreateOrUpdateSuccessResponse
  | CreateOrUpdateErrorResponse;

// Custom error class for API errors
export class IncorporationRequestError extends Error {
  statusCode: number;
  errors?: string[];

  constructor(
    message: string,
    statusCode: number,
    errors?: string[]
  ) {
    super(message);
    this.name = "IncorporationRequestError";
    this.statusCode = statusCode;
    this.errors = errors;
  }
}

/**
 * Creates or updates an incorporation request
 * @param data - Partial IncorporationRequest data. If `id` is present, updates existing request; if absent, creates new one.
 * @returns Promise resolving to success response with id and full data
 * @throws IncorporationRequestError for API errors (404, 403, 422, etc.)
 */
export async function createOrUpdateIncorporationRequest(
  data: Partial<IncorporationRequest>
): Promise<CreateOrUpdateSuccessResponse> {
  try {
    const response = await apiRequest(
      CREATE_OR_UPDATE_INCORPORATION_REQUEST_API,
      "POST",
      data,
      false,
      { throwOnError: false }
    );

    const responseData = response.data as CreateOrUpdateResponse;
    const status = response.status;
    const ok = response.ok;

    // Handle success response (200)
    if (ok && responseData.success === true) {
      return responseData;
    }

    // Handle error responses
    if (!responseData.success) {
      // 404 - Request not found
      if (status === 404) {
        throw new IncorporationRequestError(
          responseData.error || "Registro no encontrado",
          404
        );
      }

      // 403 - Unauthorized
      if (status === 403) {
        throw new IncorporationRequestError(
          responseData.error || "No autorizado para acceder a este registro",
          403
        );
      }

      // 422 - Validation errors
      if (status === 422) {
        throw new IncorporationRequestError(
          responseData.error || "Error de validación",
          422,
          responseData.errors
        );
      }

      // Other error status codes
      throw new IncorporationRequestError(
        responseData.error || `Error en la solicitud (${status})`,
        status,
        responseData.errors
      );
    }

    // Fallback for unexpected response format
    throw new IncorporationRequestError(
      "Respuesta inesperada del servidor",
      status
    );
  } catch (error) {
    // Re-throw if it's already an IncorporationRequestError
    if (error instanceof IncorporationRequestError) {
      throw error;
    }

    // Handle network errors or other fetch errors
    if (error instanceof Error) {
      throw new IncorporationRequestError(
        `Error de red: ${error.message}`,
        0
      );
    }

    // Fallback for unknown errors
    throw new IncorporationRequestError("Error desconocido", 0);
  }
}

